import { useMemo } from 'react';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { ResourceFormValues } from './ResourceModel';

/**
 * Validação do drawer de resource (T7, FR-029, FR-030).
 *
 * O que dá para saber aqui, se valida aqui: identificador no formato do
 * catálogo, toda linha com nome de tool, e nomes sem repetição **dentro do
 * resource** (o nome é um só, o do gateway: D2 do aim-cpj). A mesma tool em
 * outro site é do servidor — só ele conhece os outros resources — e volta como
 * 409 `tool_in_another_site`.
 *
 * **Endpoint segue o contrato, não o formulário:** `ResourceCreate` o exige
 * (`required: [apim_id, tipo, endpoint]`); `ResourceUpdate.endpoint` é
 * `[string, 'null']`. Um importado pode chegar sem endpoint, e exigi-lo na
 * edição travava o Salvar de um registro que o servidor aceita — com a mensagem
 * numa aba que a pessoa nem estava vendo (achado do E2E de T7).
 */

const IDENTIFIER = /^[a-z0-9-]+$/;

export interface ResourceSchemaOptions {
  /** `true` só na criação manual, que é onde o contrato exige o endpoint. */
  exigeEndpoint: boolean;
}

export function resourceSchema({
  exigeEndpoint,
}: ResourceSchemaOptions): yup.ObjectSchema<ResourceFormValues> {
  return yup.object({
    apim_id: yup
      .string()
      .trim()
      .required(t('resources.form.validation.identifierRequired'))
      .matches(IDENTIFIER, t('resources.form.validation.charset')),
    nome: yup.string().defined(),
    // Sem validação de forma: vazio é «o site padrão» (valor legítimo), e quem sabe
    // se o site EXISTE e é do tenant é o servidor — a tela só oferece os que ele
    // listou. Validar aqui daria a impressão de que o console decide isso.
    site_id: yup.string().defined(),
    tipo: yup
      .string()
      .oneOf(['mcp', 'api'] as const)
      .required(),
    endpoint: exigeEndpoint
      ? yup.string().trim().required(t('resources.form.validation.endpointRequired'))
      : yup.string().trim().defined(),
    owner: yup.string().defined(),
    environment: yup.string().defined(),
    descricao: yup.string().defined(),
    tools: yup
      .array()
      .of(
        yup.object({
          id: yup.string().nullable().defined(),
          apim_name: yup.string().trim().required(t('resources.form.validation.toolNameRequired')),
          metodo: yup
            .string()
            .oneOf(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const)
            .nullable()
            .defined(),
          path: yup.string().defined(),
          descricao: yup.string().defined(),
          // Vazio é um valor válido: significa risco **desconhecido** (FR-032).
          risk: yup
            .string()
            .oneOf(['read', 'write', 'high', ''] as const)
            .defined(),
          constrainable: yup.array().of(yup.string().required()).defined(),
          // Só leitura (aim-uig.40 D3): o Display name é do gateway, nada a validar.
          display_name: yup.string().nullable().defined(),
        }),
      )
      .defined()
      // A frase é a do formulário (§1.2 do protótipo da D2), não a do 422 `toolNameDuplicate` do servidor.
      .test('tool-name-unique', t('resources.form.validation.toolNameDuplicate'), (tools) => {
        const names = (tools ?? []).map((tool) => (tool.apim_name ?? '').trim()).filter(Boolean);
        return new Set(names).size === names.length;
      }),
  });
}

/** Resolver do formulário, revalidando a cada tecla. */
export function useResourceValidation({ exigeEndpoint }: ResourceSchemaOptions) {
  // O `locale` não aparece no corpo e ainda assim é dependência: as mensagens
  // são montadas na construção do schema, então trocar de idioma com o drawer
  // aberto deixaria a validação no idioma anterior até o próximo `mount`.
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => resourceSchema({ exigeEndpoint }), [exigeEndpoint, locale]);
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}
